
import React, { useEffect, useState } from "react";
import {Box,Typography,IconButton,Container,Paper,Avatar,Autocomplete,TextField,Chip,CircularProgress,Alert,Card,CardContent,Divider,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import CommonFieldsScreen from "./CommonFields";
import DiagnosticSample from "../Logos/DiagnoisticDelivery/DiagnosticSampleCollection.png";
import Footer from "../Common/Footer";
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";

const DIAGNOSTIC_TESTS_API = process.env.REACT_APP_GET_DIAGNOSTIC_TESTS;

const DiagnosticDeliveryScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [diagnosticTests, setDiagnosticTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      setUserLoading(true);
      
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      }

      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      if (userId && token) {
        try {
          const userData = await fetchUserFromRedis(userId, token);
          if (userData) {
            setUser(userData);
            sessionStorage.setItem("user", JSON.stringify(userData));
          }
        } catch (err) {
          console.error("Error fetching user from Redis:", err);
        }
      }
      
      setUserLoading(false);
    };

    fetchUser();
    fetchDiagnosticTests();
  }, []);

  const fetchDiagnosticTests = async () => {
    try {
      setLoading(true);
      const response = await fetch(DIAGNOSTIC_TESTS_API);
      
      if (!response.ok) {
        throw new Error("Failed to fetch diagnostic tests");
      }

      const data = await response.json();
      setDiagnosticTests(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching diagnostic tests:", err);
      setError("Failed to load diagnostic tests. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTestSelection = (event, newValue) => {
    setSelectedTests(newValue);
  };

  const handleRemoveTest = (testToRemove) => {
    setSelectedTests(selectedTests.filter((test) => test.id !== testToRemove.id));
  };

  const calculateTotal = () => {
    return selectedTests.reduce((sum, test) => sum + test.mrp, 0);
  };
  
  const calculateMRPTotal = () => {
    return selectedTests.reduce((sum, test) => sum + test.mrp, 0);
  };

  const calculateSavings = () => {
    return calculateMRPTotal() - calculateTotal();
  };

  const handleBackToServices = () => {
    sessionStorage.removeItem("commonFormData");
    navigate("/services");
  };

  return (
    <>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          {/* Header */}
          <Box mb={3}>
            <IconButton onClick={handleBackToServices}>
              <ArrowBackIosNewIcon />
            </IconButton>
            <Box display="flex" alignItems="center" mt={4}>
              <Avatar
                src={DiagnosticSample}
                alt="DIAGNOSTIC SAMPLE COLLECTION"
                sx={{ width: 60, height: 60, borderRadius: 2 }}
              />
              <Box ml={2}>
                <Typography fontSize={16} fontWeight={700} color="text.primary">
                  DIAGNOSTIC SAMPLE COLLECTION
                </Typography>
                <Typography
                  fontSize={12}
                  color="text.secondary"
                  mt={0.5}
                  sx={{ maxWidth: 220 }}
                >
                  Ayuja Will Choose Right People For The Timing Of Your Selection
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Test Selection Section */}
          <Box mb={4}>
            <Typography fontSize={18} fontWeight={600} mb={2} color="#006D77">
              Select Diagnostic Tests
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Autocomplete
                  multiple
                  options={diagnosticTests}
                  value={selectedTests}
                  onChange={handleTestSelection}
                  getOptionLabel={(option) => option.test_name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select tests..."
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ width: "100%" }}>
                        <Typography fontSize={14} fontWeight={600}>
                          {option.test_name}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                        {/* <Typography
                              fontSize={12}
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              ₹{option.mrp}
                            </Typography> */}
                          <Box display="flex" alignItems="center" gap={1}>
                          {/* <Typography fontSize={14} fontWeight={600} color="#006D77">
                              ₹{option.proposed_rate}
                            </Typography> */}
                            <Typography fontSize={14} fontWeight={600} color="#006D77">
                              ₹{option.mrp}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        label={option.test_name}
                        sx={{ bgcolor: "#E0F2F1", color: "#006D77" }}
                      />
                    ))
                  }
                  sx={{ mb: 3 }}
                />

                {/* Selected Tests Summary */}
                {selectedTests.length > 0 && (
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography fontSize={16} fontWeight={600} mb={2}>
                        Selected Tests ({selectedTests.length})
                      </Typography>

                      {selectedTests.map((test) => (
                        <Box
                          key={test.id}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={1.5}
                          sx={{ borderBottom: "1px solid #f0f0f0" }}
                        >
                          <Box flex={1}>
                            <Typography fontSize={14} fontWeight={600}>
                              {test.test_name}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box textAlign="right">
                                                            {/* <Typography
                                fontSize={12}
                                sx={{ textDecoration: "line-through", color: "text.secondary" }}
                              >
                                ₹{test.mrp}
                              </Typography> */}
                              <Typography
                                fontSize={12}
                                sx={{  color: "text.secondary" }}
                              >
                                ₹{test.mrp}
                              </Typography>
                              {/* <Typography fontSize={14} fontWeight={600} color="#006D77">
                                ₹{test.proposed_rate}
                              </Typography> */}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveTest(test)}
                              sx={{ color: "#DC2626" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      <Divider sx={{ my: 2 }} />

                      {/* Price Summary */}
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography fontSize={14} color="text.secondary">
                            MRP Total:
                          </Typography>
                          <Typography fontSize={14}>
                            ₹{calculateMRPTotal().toFixed(2)}
                          </Typography>
                        </Box>
                        {/* <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography fontSize={14} color="success.main" fontWeight={600}>
                            You Save:
                          </Typography>
                          <Typography fontSize={14} color="success.main" fontWeight={600}>
                            ₹{calculateSavings().toFixed(2)}
                          </Typography>
                        </Box> */}
                        <Box display="flex" justifyContent="space-between" mt={2}>
                          <Typography fontSize={18} fontWeight={700}>
                            Total Amount:
                          </Typography>
                          <Typography fontSize={18} fontWeight={700} color="#006D77">
                            ₹{calculateTotal().toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </Box>

          {/* Form Section - Only render when user data is loaded */}
          {userLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <CommonFieldsScreen
              userData={user}
              serviceData={{
                serviceTitle: "DIAGNOSTIC SAMPLE COLLECTION",
                serviceImage: DiagnosticSample,
                servicePrice: calculateTotal(),
                orderType: "diagnostic",
                diagnosticOrderData: {
                  tests: selectedTests.map(test => ({
                    test_id: test.id,
                    test_code: test.test_code,
                    test_name: test.test_name,
                    mrp: test.mrp,
                    proposed_rate: test.proposed_rate,
                    category: test.category,
                  })),
                  total_amount: calculateTotal(), 
                  mrp_total: calculateMRPTotal(),
                  savings: calculateSavings(),
                }
              }}
            />
          )}
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default DiagnosticDeliveryScreen;